import { useState, useEffect } from 'react';
import completedSurveyService from '../services/completedSurveyService';
import surveyActivationService from '../services/surveyActivationService';
import participantService from '../services/participantService';
import { parseSimulatedDate } from '../utils/dateUtils';

export const useMergedSurveys = (participantId, simulatedDate = null) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mergedSurveys, setMergedSurveys] = useState({ 
    visitSurveys: [],
    allSurveys: [], // Store all surveys for debugging
    assessments: [] // Store data from all-assessments endpoint
  });
  const [currentSimulatedDate, setCurrentSimulatedDate] = useState(simulatedDate);
  
  // Helper function to format schedule types consistently
  const formatScheduleType = (scheduleType) => {
    const scheduleTypeMap = {
      'daily': 'Daily',
      'patient_prompted': 'Patient Prompted',
      'scheduled': 'Scheduled',
      'site_prompted': 'Site Prompted',
      'other': 'Other'
    };
    
    // If the schedule type includes a known type, return the formatted version
    if (!scheduleType) return 'Unknown';
    
    for (const [key, value] of Object.entries(scheduleTypeMap)) {
      if (scheduleType.toLowerCase().includes(key)) {
        return value;
      }
    }
    
    // If not found, return the original
    return scheduleType;
  };
  
  // Helper function to standardize assessment objects from any source
  const standardizeAssessment = (assessment, source) => {
    if (!assessment) return null;
    
    // Extract visit information consistently
    const visitNumber = assessment?.metadata?.visit_number || 
                      assessment?.metadata?.visit_identifier ||
                      assessment?.visit_number ||
                      assessment?.visit_id ||
                      (assessment?.visit && assessment.visit.replace('Visit ', '')) ||
                      null;
    
    // Determine survey name consistently
    const surveyName = assessment?.survey_name || 
                      assessment?.name || 
                      assessment?.survey ||
                      assessment?.survey_details?.name ||
                      'Unnamed Survey';
    
    // Extract date information consistently
    const availableAt = assessment?.start_time || 
                      assessment?.available_at || 
                      assessment?.date ||
                      assessment?.created_at ||
                      null;
    
    // Extract completion date
    const completedAt = assessment?.completed_at || 
                      assessment?.completion_time ||
                      assessment?.completion_datetime ||
                      null;
                      
    // Determine schedule type
    const scheduleType = assessment?.schedule_type || 
                        assessment?.metadata?.schedule_type ||
                        'Unknown';
    
    // Determine status
    const status = (assessment?.status || 
                  assessment?.metadata?.status ||
                  (completedAt ? 'completed' : 'open'))?.toLowerCase();
    
    // Determine if patient prompted or daily based on schedule type
    const isPatientPrompted = scheduleType?.toLowerCase().includes('patient_prompted');
    const isDaily = scheduleType?.toLowerCase().includes('daily');
    
    return {
      ...assessment, // Keep original fields for reference
      source, // Track data source for debugging
      survey_name: surveyName,
      visit_id: visitNumber,
      visit_number: visitNumber,
      available_at: availableAt,
      completed_at: completedAt,
      schedule_type: scheduleType,
      formatted_schedule_type: formatScheduleType(scheduleType),
      status,
      isCompleted: status === 'completed',
      isPatientPrompted,
      isDaily,
      // Add proper metadata access
      metadata: assessment?.metadata || assessment?.response_data?.metadata || {}
    };
  };

  // Define fetchMergedSurveys outside of useEffect so it can be accessed by refetch
  const fetchMergedSurveys = async (bypassCache = true, newSimulatedDate = null) => {
    console.log('Starting to fetch merged surveys for participantId:', participantId);
    setLoading(true);
    
    try {
      console.log('About to call services');
      
      // For dates with spaces (like "America/New_York"), make sure they're properly encoded
      let safeSimulatedDate = newSimulatedDate;
      if (safeSimulatedDate && typeof safeSimulatedDate === 'string') {
        // The API is having trouble with spaces in timezone names
        // Let's make sure it's properly formatted
        console.log('Original simulated date:', safeSimulatedDate);
        
        // Ensure the date is properly encoded for API calls
        safeSimulatedDate = safeSimulatedDate.trim();
        console.log('Processed simulated date for API:', safeSimulatedDate);
      }

      // Fetch all data in parallel for better performance
      console.log('Fetching all survey data...');
      
      /*
      // Start all data fetches in parallel
      const [completedDataPromise, openDataPromise, assessmentsPromise] = [
          // Completed surveys
          completedSurveyService.getCompletedSurveys(
            participantId, 
            bypassCache, 
            safeSimulatedDate || currentSimulatedDate, 
            {}, 
            false // Pass includeQuestions=false to optimize payload size
          ),
          
          // Open surveys
          surveyActivationService.getAllOpenSurveysForActivation(
            participantId, 
            bypassCache, 
            safeSimulatedDate || currentSimulatedDate
          ),
          
          // All assessments (new endpoint)
          // By default don't pass the showFutureAssessments parameter
          // Backend will handle permissions and only show future assessments to internal users
          // bypassCache = true, includeResponses = false, showFutureAssessments = false
          participantService.getAllAssessmentsForParticipant(
            participantId,
            bypassCache,
            false, // don't include responses by default
            false, // showFutureAssessments
            safeSimulatedDate || currentSimulatedDate
          )
        ];
        
        // Wait for all promises to resolve - now all endpoints return data in the same format
      const [completedData, openData, assessmentsData] = await Promise.all([
        completedDataPromise.catch(error => {
          console.error('Error fetching completed surveys:', error);
          return { count: 0, results: [] }; // Return empty paginated response on error
        }),
        openDataPromise.catch(error => {
          console.error('Error fetching open surveys:', error);
          return { count: 0, results: [] }; // Return empty paginated response on error
        }),
        assessmentsPromise.catch(error => {
          console.error('Error fetching all assessments:', error);
          return { count: 0, results: [] }; // Return empty paginated response on error
        })
      ]);
      */

      const completedData = []
      const openData = []

      const [assessmentsPromise] = [
        // All assessments (new endpoint)
        // By default don't pass the showFutureAssessments parameter
        // Backend will handle permissions and only show future assessments to internal users
        // bypassCache = true, includeResponses = false, showFutureAssessments = false
        participantService.getAllAssessmentsForParticipant(
          participantId,
          bypassCache,
          false, // don't include responses by default
          true, // showFutureAssessments — All Assessments tab should show all windows
          safeSimulatedDate || currentSimulatedDate
        )
      ];
      
      // Wait for all promises to resolve - now all endpoints return data in the same format
    const [assessmentsData] = await Promise.all([
      assessmentsPromise.catch(error => {
        console.error('Error fetching all assessments:', error);
        return { count: 0, results: [] }; // Return empty paginated response on error
      })
    ]);

      console.log('All assessments API response:', assessmentsData);

      // Extract data from standardized responses - all endpoints now use the same format
      const completedSurveys = completedData?.results || [];
      const openSurveys = openData?.results || [];
      const assessmentsRaw = assessmentsData?.results || [];
        
        console.log('Completed surveys:', completedSurveys);
        console.log('Open surveys:', openSurveys);
        
        // Process completed surveys using the standardization helper
      const markedCompletedSurveys = completedSurveys
        .map(survey => standardizeAssessment(survey, 'completed'))
        .filter(Boolean);
        
      // Process open surveys using the standardization helper
      const markedOpenSurveys = openSurveys
        .map(survey => {
          // Standard fields processing
          const standardized = standardizeAssessment(survey, 'open');
          if (!standardized) return null;
          
          // Trust the server's status for missed determination.
          // The all-assessments API uses participant timezone-aware window times
          // to compute status correctly. Don't override on the client.
          return {
            ...standardized,
            status: standardized.status
          };
        })
        .filter(Boolean);
        
        // Combine all surveys and add unique keys for React
        const allSurveys = [...markedCompletedSurveys, ...markedOpenSurveys].map((survey, index) => {
          // Add a display name if missing
          if (!survey.survey_name && !survey.name) {
            survey.survey_name = `Survey ${index + 1}`;
          }
          return survey;
        });
        
        // Group by visit (using visit_number we extracted)
        const visitGroups = {};
        
        // Debug: log all surveys before grouping
        console.log('✅ All surveys before grouping:', allSurveys.map(s => ({
          id: s.id,
          name: s.survey_name,
          visitId: s.visit_id || s.visit_number,
          status: s.status,
          date: s.available_at || s.start_time || s.created_at
        })));
        
        // Instead, we'll create groups naturally based on the data
        
        // Use survey date to help determine appropriate visit organization
        allSurveys.forEach(survey => {
          // Skip null/undefined surveys
          if (!survey) return;
          
          // Extract visit ID from all possible locations
          let visitId = survey?.visit_number || 
                       survey?.visit_id || 
                       survey?.metadata?.visit_number || 
                       survey?.metadata?.visit_id || 
                       'unassigned';
          
          // Check if visit_type exists and is a patient prompted survey type
          const isPatientPrompted = 
            (survey?.visit_type && survey.visit_type.includes('patient_prompted')) ||
            (survey?.metadata?.visit_type && survey.metadata.visit_type.includes('patient_prompted')) ||
            (survey?.schedule_type && survey.schedule_type.includes('patient_prompted')) ||
            (survey?.metadata?.schedule_type && survey.metadata.schedule_type.includes('patient_prompted'));
            
          // Check if the survey is a daily survey type
          const isDaily = 
            (survey?.visit_type && survey.visit_type.includes('daily')) ||
            (survey?.metadata?.visit_type && survey.metadata.visit_type.includes('daily')) ||
            (survey?.schedule_type && survey.schedule_type.includes('daily')) ||
            (survey?.metadata?.schedule_type && survey.metadata.schedule_type.includes('daily'));
          
          // Extract date from all possible fields
          const surveyDate = survey?.start_time || 
                             survey?.available_at || 
                             survey?.created_at || 
                             null;
          
          // If patient prompted, group under patient prompted rather than visit
          if (isPatientPrompted) {
            visitId = 'patient_prompted';
          } else if (isDaily) {
            visitId = 'daily';
          }
          
          // Normalize the visit ID (ensure numeric visit IDs stay as is)
          let adjustedVisitId = visitId;
          
          // Use the adjusted visit ID as the key for the visit group
          console.log(`Organizing survey ${survey.survey_name}:`, {
            originalVisitId: visitId,
            adjustedVisitId,
            date: surveyDate,
            status: survey.status
          });
          
          // Create special group names for non-numeric visit IDs
          let groupName = '';
          if (adjustedVisitId === 'patient_prompted') {
            groupName = 'Patient Prompted Assessments';
          } else if (adjustedVisitId === 'daily') {
            groupName = 'Daily Assessments';
          } else if (adjustedVisitId === 'unassigned') {
            groupName = 'Unassigned Surveys';
          } else {
            groupName = `Visit ${adjustedVisitId}`;
          }
          
          if (!visitGroups[adjustedVisitId]) {
            visitGroups[adjustedVisitId] = {
              id: adjustedVisitId,
              originalVisitId: adjustedVisitId, // Keep track of the original ID
              name: groupName,
              surveys: [],
              date: surveyDate ? new Date(surveyDate) : null // Store date for sorting
            };
          }
          visitGroups[adjustedVisitId].surveys.push(survey);
        });
        
        // Debug: log the visit groups after organization
        console.log('✅ Visit groups after organization:', Object.values(visitGroups).map(g => ({
          id: g.id,
          name: g.name,
          originalVisitId: g.originalVisitId,
          surveysCount: g.surveys.length,
          date: g.date,
          surveys: g.surveys.map(s => ({
            id: s.id,
            name: s.survey_name,
            status: s.status
          }))
        })));
        
        // Convert to array for easier mapping in UI
        const visitSurveysArray = Object.values(visitGroups);
        
        // Sort by date and visit number
        visitSurveysArray.sort((a, b) => {
          // Handle 'unassigned' specially
          if (a?.id.includes('unassigned')) return 1;
          if (b?.id.includes('unassigned')) return -1;
          
          // If one has a date and the other doesn't, date takes precedence
          if (a?.date && !b?.date) return -1;
          if (!a?.date && b?.date) return 1;
          
          // If both have dates, sort by date
          if (a?.date && b?.date) {
            // Get the original visit IDs for comparison
            const visitIdA = a?.originalVisitId || '';
            const visitIdB = b?.originalVisitId || '';
            
            // If same visit ID, sort by date
            if (visitIdA === visitIdB) {
              return a.date - b.date;
            }
            
            // Different visit IDs - sort by ID first
            const numA = parseInt(visitIdA, 10);
            const numB = parseInt(visitIdB, 10);
            
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
          }
          
          // Fallback to original visit ID comparison
          const numA = parseInt(a?.originalVisitId, 10);
          const numB = parseInt(b?.originalVisitId, 10);
          
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          
          // Final fallback - compare string IDs
          return String(a?.id).localeCompare(String(b?.id));
        });
        
        // Sort surveys within each visit by name
        visitSurveysArray.forEach(visit => {
          // Skip if surveys array is not defined
          if (!Array.isArray(visit?.surveys)) return;
          
          // Use safe sorting to avoid errors with undefined properties
          visit.surveys.sort((a, b) => {
            // Handle null or undefined survey_name values
            const aName = a?.survey_name || 'Unknown';
            const bName = b?.survey_name || 'Unknown';
            return aName.localeCompare(bName);
          });
        });
        
        // Process assessments data using the standardization helper
        const formattedAssessments = assessmentsRaw
          .map(assessment => standardizeAssessment(assessment, 'assessment'))
          .filter(Boolean)
          .map(assessment => ({
            ...assessment,
            // Add any assessment-specific fields needed by components
            visitName: assessment.visit || `Visit ${assessment.visit_number || 'unassigned'}`,
            relevantDate: assessment.available_at
          }));
        
        setMergedSurveys({
          visitSurveys: visitSurveysArray,
          allSurveys: allSurveys, // Store all surveys for debugging
          assessments: formattedAssessments // Store formatted assessments data
        });
    } catch (err) {
      setError(err);
      console.error('Error fetching surveys:', err);
      // Even on error, set a default empty structure so the UI doesn't break
      setMergedSurveys({ visitSurveys: [], assessments: [] });
    } finally {
      setLoading(false);
    }
  };

  // Function to refetch data with a new simulated date
  const refetch = async (bypassCache = true, newSimulatedDate = null) => {
    setCurrentSimulatedDate(newSimulatedDate);
    setLoading(true);
    
    try {
      await fetchMergedSurveys(bypassCache, newSimulatedDate);
    } catch (err) {
      console.error('Error refetching surveys:', err);
      setError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useMergedSurveys hook running with participantId:', participantId, 'simulated date:', currentSimulatedDate);

    if (participantId) {
      fetchMergedSurveys(true, currentSimulatedDate);
    } else {
      // If no participantId, just set loading to false and use empty data
      setLoading(false);
      setMergedSurveys({ visitSurveys: [] });
    }
  }, [participantId, currentSimulatedDate]);

  return { mergedSurveys, loading, error, refetch };
};